// ----------------------------------------------------------------------------------------------
// This file is a Typescript conversion loosely based on the MIT Licensed Javascript library
// called `register-service-worker` which was published by "Yuxi (Evan) You"
// at https://github.com/yyx990803/register-service-worker/blob/master/src/index.js
// with the MIT license https://github.com/yyx990803/register-service-worker/blob/master/LICENSE
// ----------------------------------------------------------------------------------------------

// Register a service worker to serve assets from local cache.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on the "N+1" visit to a page, since previously
// cached resources are updated in the background.

const isLocalhost = () => Boolean(
  window.location.hostname === 'localhost' ||
      // [::1] is the IPv6 localhost address.
      window.location.hostname === '[::1]' ||
      // 127.0.0.1/8 is considered localhost for IPv4.
      window.location.hostname.match(
        /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
      )
);

const waitWindowLoad = new Promise(resolve => window.addEventListener('load', resolve));

export type HookArg = ServiceWorkerRegistration | Error | string | undefined;

export type Hooks = {
    registrationOptions?: RegistrationOptions;

    /**
     * Called when the initial service worker is in a ready (active) state after page load.
     * It is called wether the page is controlled by it or not.
     * It is not called on subsequent service worker updates
     * but it will be called each times the page reloads.
     */
    ready?: (registration: ServiceWorkerRegistration) => void;

    /**
     * Called when the service worker has been successfully registered
     * (this does not mean it is ready).
     */
    registered?: (registration: ServiceWorkerRegistration) => void;

    /**
     * Called when a service worker has just completed installation
     * (meaning the new pages have been cached) and will likely be waiting
     * for activation (meaning switching to it and dropping the old cache).
     * Note: Activation will be automatic if no pages are currently controlled by
     *       the service worker.
     */
    installed?: (registration: ServiceWorkerRegistration) => void;

    /**
     * Called when a service worker has been installed and activated.
     * This means the SW has been updated and the old cache deleted.
     *
     * This applies to pages that were already controlled by a service worker.
     * This means page needs to be reloaded ASAP to catch up and avoid
     * file/resource not found issues (the old pages are not anymore on the server).
     *
     * Pages that were not under the control of the SW also see the change but
     * they are not affected by it (not until they reload) as only controlled
     * pages use the SW cache.
     */
    updated?: (registration: ServiceWorkerRegistration) => void;

    /**
     * Called by the Service Worker when a client (i.e. a page) is about to trigger
     * the activation process.
     * Any page in a busy state (user activity, meeting in progress, etc) should immediately
     * object to the activation by sending the originating page a REJECT_ACTIVATION message.
     * @param senderId The ID identifying the client that is attempting to trigger the activation process.
     */
    preActivationWarning?: (senderId: string) => void;

    rejectActivation?: () => void;

    error?: (error: Error) => void;
  };

let hooks: Hooks = {};

const emit = (hookName: keyof Hooks, arg?: HookArg) => {
  if (hookName === 'registrationOptions') {
    return;
  }
  if (hooks && hooks[hookName]) {
    const hook = hooks[hookName];
    switch (hookName) {
      case 'error':
        if ((hooks.error !== undefined) && (arg instanceof Error)) {
          hooks.error(arg);
        }
        break;
      case 'preActivationWarning':
        if ((hooks.preActivationWarning !== undefined) && (typeof arg === 'string')) {
          hooks.preActivationWarning(arg);
        }
        break;
      case 'rejectActivation':
        if (hooks.rejectActivation !== undefined) {
          hooks.rejectActivation();
        }
        break;
      default:
        if ((hook !== undefined) && (arg instanceof ServiceWorkerRegistration)) {
          const hookToCall = hooks[hookName] as (registration: ServiceWorkerRegistration) => void;
          hookToCall(arg);
        }
        break;
    }
  }
};

export function register(swUrl: string, clientHooks: Hooks = {}): void {
  hooks = clientHooks;
  const { registrationOptions = {} } = hooks;
  delete hooks.registrationOptions;

  if ('serviceWorker' in navigator) {
    waitWindowLoad.then(() => {
      if (isLocalhost()) {
        // This is running on localhost. Lets check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, registrationOptions);
        navigator.serviceWorker.ready.then(registration => {
          emit('ready', registration);
        }).catch(error => handleError(error));
      } else {
        // Is not local host. Just register service worker
        registerValidSW(swUrl, registrationOptions);
        navigator.serviceWorker.ready.then(registration => {
          emit('ready', registration);
        }).catch(error => handleError(error));
      }
      navigator.serviceWorker.addEventListener('message', handleMessage);
    });
  }
}

export function unregister(): void {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.removeEventListener('message', handleMessage);
    navigator.serviceWorker.getRegistration().then(registration => {
      if (registration) {
        registration.unregister();
      }
    }).catch(error => handleError(error));
  }
}

function handleMessage(event: MessageEvent): void {
  if (event.data) {
    if (event.data.type === 'PRE_ACTIVATION_WARNING') {
      emit('preActivationWarning', event.data.sender);
    }
    if (event.data.type === 'REJECT_ACTIVATION') {
      emit('rejectActivation');
    }
  }
}

function handleError(error: Error) {
  emit('error', error);
}

function registerValidSW(swUrl: string, registrationOptions?: RegistrationOptions): void {
  navigator.serviceWorker
    .register(swUrl, registrationOptions)
    .then(registration => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (!installingWorker) {
          emit('error', new Error(
            'Got a null installing worker on updatefound notification. This is against the ServiceWorker specs'));
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            // At this point, the fresh content has been added to the cache
            // but the old one is still available in the cache.
            // Need to wait for the application to be idle to trigger the activation process.
            emit('installed', registration);
          } else if (installingWorker.state === 'activated') {
            emit('updated', registration);
          }
        };
      };
      emit('registered', registration);
      if (registration.waiting && !registration.installing) {
        // There is a service worker already waiting, so we need to request its activation...
        // If there is a new one being installed it is better to just wait for that one to complete.
        //
        // But first we need to ensure there will be an event handler in place (the browser could have
        // been in the the middle of installing the SW in the background whilst the user opened Fender.
        // And this means we would not be notified by onupdatefound, so no handler would have been set).
        const waitingWorker = registration.waiting;
        if (!waitingWorker.onstatechange) {
          waitingWorker.onstatechange = () => {
            if (waitingWorker.state === 'activated') {
              emit('updated', registration);
            }
          };
        }
        emit('installed', registration);
      }
    })
    .catch(error => handleError(error));
}

function checkValidServiceWorker(swUrl: string, registrationOptions?: RegistrationOptions): void {
  // Check if the service worker can be found.
  fetch(swUrl)
    .then(response => {
      // Ensure service worker exists, and that we really are getting a JS file.
      if (response.status === 404) {
        // No service worker found.
        emit('error', new Error(`Service worker not found at ${swUrl}`));
        unregister();
      } else if (response.headers.get('content-type')?.indexOf('javascript') === -1) {
        emit('error', new Error(
          `Expected ${swUrl} to have javascript content-type, ` +
            `but received ${response.headers.get('content-type')}`));
        unregister();
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, registrationOptions);
      }
    })
    .catch(error => handleError(error));
}
